import { useState, useCallback, useRef, useEffect } from 'react';
import { useEditor, Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import { AppNode, NodeType, RichTextContent, TextEditMode, NodeSize } from '@/types';
import { setEditorInstance } from '@/stores/nodeStore';
import { startNodeResize, endNodeResize } from '@/components/flow/FlowEditor';

export interface UseSelfWriteEditorOptions {
  data: AppNode;
  onNodeUpdate: (id: string, data: Partial<AppNode['data']>, size?: { width?: number; height?: number }, title?: string) => void;
}

const DEBOUNCE_MS = 500;

export function useSelfWriteEditor(options: UseSelfWriteEditorOptions) {
  const { data, onNodeUpdate } = options;
  const isSelfWrite = data.type === NodeType.PROMPT_INPUT && data.data.subMode === 'self-write';
  const editMode: TextEditMode = isSelfWrite ? (data.data.editMode as TextEditMode) || 'preview' : 'preview';
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onNodeUpdateRef = useRef(onNodeUpdate);
  onNodeUpdateRef.current = onNodeUpdate;
  const dataIdRef = useRef(data.id);
  dataIdRef.current = data.id;

  // 编辑器实例引用（用于在回调中访问最新编辑器）
  const editorRef = useRef<Editor | null>(null);

  // 编辑模式切换
  const enterEdit = useCallback(() => {
    onNodeUpdateRef.current(dataIdRef.current, { editMode: 'edit' });
    // 立即聚焦编辑器，保持当前滚动位置不跳转
    requestAnimationFrame(() => {
      const ed = editorRef.current;
      if (ed && !ed.isFocused) {
        ed.commands.focus();
      }
    });
  }, []);

  const enterFullscreen = useCallback(() => {
    onNodeUpdateRef.current(dataIdRef.current, { editMode: 'fullscreen' });
  }, []);

  const exitToPreview = useCallback(() => {
    onNodeUpdateRef.current(dataIdRef.current, { editMode: 'preview' });
  }, []);

  const exitToEdit = useCallback(() => {
    onNodeUpdateRef.current(dataIdRef.current, { editMode: 'edit' });
  }, []);

  // 尺寸调整（直接更新，resize手柄已有rAF节流）
  const handleResize = useCallback((size: NodeSize) => {
    onNodeUpdateRef.current(dataIdRef.current, { nodeSize: size }, { width: size.width, height: size.height });
  }, []);

  // 编辑器实例（依赖只有 isSelfWrite：模式切换不重建编辑器）
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Placeholder.configure({
        placeholder: '输入内容...',
        emptyEditorClass: 'is-empty',
      }),
    ],
    content: data.data.richContent || {
      type: 'doc',
      content: [{ type: 'paragraph' }],
    },
    editable: editMode === 'edit' || editMode === 'fullscreen',
    autofocus: false,
    onUpdate: ({ editor }) => {
      if (!isSelfWrite) return;
      if (debounceRef.current) clearTimeout(debounceRef.current);
      const json = editor.getJSON() as RichTextContent;
      const text = editor.getText();
      debounceRef.current = setTimeout(() => {
        onNodeUpdateRef.current(dataIdRef.current, { richContent: json, textContent: text });
      }, DEBOUNCE_MS);
    },
  }, [isSelfWrite]);

  // 同步编辑器实例到 ref
  useEffect(() => {
    editorRef.current = editor;
  }, [editor]);

  // 外部内容变化时同步到编辑器（避免与 onUpdate 的保存循环）
  useEffect(() => {
    if (!editor || !isSelfWrite) return;
    const externalContent = data.data.richContent;
    if (!externalContent) return;
    const currentContent = editor.getJSON();
    const currentText = editor.getText();
    const externalText = data.data.textContent || '';

    const contentChanged =
      JSON.stringify(currentContent) !== JSON.stringify(externalContent) &&
      currentText.trim() !== externalText.trim();

    if (contentChanged) {
      editor.commands.setContent(externalContent, { emitUpdate: false });
    }
  }, [editor, data.data.richContent, data.data.textContent, isSelfWrite]);

  // 根据模式切换 editable + 焦点
  useEffect(() => {
    if (!editor) return;
    editor.setEditable(editMode === 'edit' || editMode === 'fullscreen');
    if (editMode === 'edit' && !editor.isFocused) {
      editor.commands.focus('end');
    }
  }, [editor, editMode]);

  // 注册/清理编辑器实例到缓存
  useEffect(() => {
    if (editor && isSelfWrite) {
      setEditorInstance(data.id, editor);
    }
    return () => {
      if (isSelfWrite) {
        setEditorInstance(data.id, null);
      }
    };
  }, [editor, data.id, isSelfWrite]);

  // 组件卸载时强制保存（避免 store 销毁时调用）
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (editor && isSelfWrite) {
        try {
          const json = editor.getJSON() as RichTextContent;
          const text = editor.getText();
          onNodeUpdateRef.current(dataIdRef.current, { richContent: json, textContent: text });
        } catch {
          // ignore
        }
      }
    };
  }, [editor, data.id, isSelfWrite]);

  // ESC 键退出编辑模式（仅在编辑模式下生效）
  useEffect(() => {
    if (!isSelfWrite || (editMode !== 'edit' && editMode !== 'fullscreen')) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      // 仅在焦点不在输入框时处理 ESC
      if (e.key === 'Escape' && !(e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement)) {
        if (editMode === 'fullscreen') {
          exitToEdit();
        } else if (editMode === 'edit') {
          exitToPreview();
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isSelfWrite, editMode, exitToEdit, exitToPreview]);

  // 全屏模式阻止滚动穿透
  useEffect(() => {
    if (!isSelfWrite || editMode !== 'fullscreen') return;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = originalOverflow; };
  }, [isSelfWrite, editMode]);

  return {
    editMode,
    editor: isSelfWrite ? editor : null,
    enterEdit,
    enterFullscreen,
    exitToPreview,
    exitToEdit,
    handleResize,
  };
}
